import { TeamHeader, SuccessStories, TeamCarousel, GradStudents } from "./components";
import { researchers, Researcher } from "../../constants/ourTeam/researchers";
import { useTranslation } from "../../components/Translation";
import { localizeName } from "../../utils/localizeName";
import { useState, useEffect } from "react";
import LicStudents from "./components/LicStudents";

function ResearcherModal({ researcher: r, onClose }: { researcher: Researcher; onClose: () => void }) {
  const { t, language } = useTranslation();
  const [photoLeft] = useState(() => Math.random() > 0.5);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const PhotoBlock = (
    <div className="relative w-full md:w-2/5 flex-shrink-0 min-h-64 md:min-h-full bg-gray-100">
      <img src={r.image} alt={r.name} className="w-full h-full object-cover object-top" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
      <span className="absolute top-3 left-3 bg-amber-500 text-white text-micro font-bold px-2 py-0.5 rounded">
        {r.badge}
      </span>
      <div className="absolute bottom-4 left-4 right-4">
        <span className={`text-micro text-white font-semibold px-2 py-0.5 rounded ${r.roleColor} inline-block mb-1`}>
          {t(`researchers.${r.roleKey}`)}
        </span>
        <h3 className="text-white text-lg font-bold leading-tight">{localizeName(r.name, language)}</h3>
      </div>
    </div>
  );

  const TextBlock = (
    <div className="flex-1 p-6 overflow-y-auto space-y-4">
      <p className="text-mini text-gray-600 leading-relaxed text-justify">
        {t(`researchers.${r.descriptionKey}`)}
      </p>

      {r.specialtyKeys && r.specialtyKeys.length > 0 && (
        <div>
          <p className="text-micro font-bold text-azul-uady uppercase tracking-wider mb-2">
            {t('researchers.investigador_especialidades')}
          </p>
          <div className="flex flex-wrap gap-2">
            {r.specialtyKeys.map((key, i) => (
              <span key={i} className="text-micro bg-azul-uady/10 text-azul-uady font-semibold px-3 py-1 rounded-full">
                {t(`researchers.${key}`)}
              </span>
            ))}
          </div>
        </div>
      )}

      {r.email && (
        <div>
          <p className="text-micro font-bold text-azul-uady uppercase tracking-wider mb-2">
            {t('researchers.investigador_contacto')}
          </p>
          <a href={`mailto:${r.email}`} className="text-mini text-dorado-uady hover:underline">
            {r.email}
          </a>
        </div>
      )}

      <div className="flex items-center justify-between pt-2 gap-3">
        {/* Redes sociales */}
        <div className="flex items-center gap-2">
          {r.socialLinks?.linkedin && (
            <a href={r.socialLinks.linkedin} target="_blank" rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center rounded-full bg-[#0077b5]/10 hover:bg-[#0077b5] group transition-colors"
              title="LinkedIn"
            >
              <svg className="w-4 h-4 text-[#0077b5] group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            </a>
          )}
          {r.socialLinks?.github && (
            <a href={r.socialLinks.github} target="_blank" rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-800 group transition-colors"
              title="GitHub"
            >
              <svg className="w-4 h-4 text-gray-700 group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0 1 12 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z" />
              </svg>
            </a>
          )}
          {r.socialLinks?.researchgate && (
            <a href={r.socialLinks.researchgate} target="_blank" rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center rounded-full bg-[#00ccbb]/10 hover:bg-[#00ccbb] group transition-colors"
              title="ResearchGate"
              aria-label={`ResearchGate: ${localizeName(r.name, language)}`}
            >
              <svg aria-hidden="true" className="w-4 h-4 text-[#00ccbb] group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.586 0c-.818 0-1.508.19-2.073.565-.563.377-.97.936-1.213 1.68a12.193 12.193 0 0 0-.368 3.12c0 .427.03.845.09 1.246-.58-.023-1.175-.036-1.787-.036-5.724 0-9.217 3.26-9.217 8.735 0 5.464 3.493 8.721 9.217 8.721 5.715 0 9.208-3.257 9.208-8.721 0-2.739-.81-4.974-2.34-6.55.12-.46.185-.96.185-1.495 0-.777-.163-1.49-.487-2.135-.324-.644-.792-1.158-1.4-1.536C19.786.19 19.586 0 19.586 0zm-6.351 9.97c1.322 0 2.553.235 3.54.648-.122.633-.194 1.292-.194 1.982 0 4.524-2.793 7.232-7.217 7.232-4.42 0-7.218-2.708-7.218-7.232 0-4.531 2.798-7.245 7.218-7.245.622 0 1.226.065 1.804.185-.067.462-.1.935-.1 1.416 0 1.008.167 1.956.467 2.829a7.544 7.544 0 0 0-1.3-.11c-2.547 0-4.086 1.54-4.086 4.125 0 2.576 1.539 4.11 4.086 4.11 2.54 0 4.075-1.534 4.075-4.11 0-.21-.012-.414-.034-.613-.42.163-.878.252-1.357.252-1.967 0-3.167-1.202-3.167-3.228 0-.418.06-.814.17-1.181.654.232 1.362.358 2.1.358.74 0 1.447-.126 2.1-.358.11.367.17.763.17 1.181 0 2.026-1.2 3.228-3.167 3.228z" />
              </svg>
            </a>
          )}
        </div>

        {/* Botón ver perfil */}
        {r.cvUrl && (
          <a
            href={r.cvUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-dorado-uady text-white text-mini font-semibold px-5 py-2.5 rounded hover:bg-yellow-600 transition-colors whitespace-nowrap"
          >
            {t('researchers.investigador_ver_perfil')}
          </a>
        )}
      </div>



    </div >
  );

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row max-h-[90vh] relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center bg-black/40 hover:bg-black/60 rounded-full text-white transition-colors"
        >
          ✕
        </button>
        {photoLeft ? (
          <>{PhotoBlock}{TextBlock}</>
        ) : (
          <>{TextBlock}{PhotoBlock}</>
        )}
      </div>
    </div>
  );
}

export default function TeamSection() {
  const { t, language } = useTranslation();
  const [selectedResearcher, setSelectedResearcher] = useState<Researcher | null>(null);

  return (
    <section>
      <TeamHeader />

      <div className="py-14 px-6 max-w-6xl mx-auto">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-1 h-6 bg-dorado-uady rounded" />
          <h2 className="text-2xl font-bold text-azul-uady">
            {t('researchers.equipo_titulo')}
          </h2>
        </div>
        <p className="text-gray-400 text-mini mb-8">
          {t('researchers.equipo_subtitulo')}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {researchers.map((r, i) => (
            <div key={i} onClick={() => setSelectedResearcher(r)} className="group bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md hover:scale-[1.02] transition-all duration-300 flex flex-col cursor-pointer">
              <div className="relative h-52 bg-gray-100 flex-shrink-0">
                <img
                  src={r.image}
                  alt={r.name}
                  className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-500"
                />
                <span className="absolute top-2 right-2 bg-amber-500 text-white text-micro font-bold px-2 py-0.5 rounded">
                  {r.badge}
                </span>
              </div>

              <div className="p-4 flex flex-col flex-1">
                <h3 className="font-bold text-azul-uady text-mini mb-1">{localizeName(r.name, language)}</h3>
                <div className="h-6 mb-2">
                  <span className={`text-micro text-white font-semibold px-2 py-0.5 rounded ${r.roleColor} inline-block line-clamp-1`}>
                    {t(`researchers.${r.roleKey}`)}
                  </span>
                </div>
                <p className="text-micro text-gray-500 leading-relaxed line-clamp-3 flex-1 text-justify self-start">
                  {t(`researchers.${r.descriptionKey}`)}
                </p>
                {r.socialLinks?.researchgate && (
                  <a
                    href={r.socialLinks.researchgate}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`ResearchGate: ${localizeName(r.name, language)}`}
                    onClick={event => event.stopPropagation()}
                    className="mt-4 inline-flex w-fit items-center gap-2 rounded-full bg-[#00ccbb]/10 px-3 py-1.5 text-micro font-semibold text-[#008f85] transition-colors hover:bg-[#00ccbb] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00a99d] focus-visible:ring-offset-2"
                  >
                    <span aria-hidden="true">RG</span>
                    <span>ResearchGate</span>
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Estudiantes de posgrado */}
      <div className="border-t border-gray-100">
        <GradStudents />
      </div>

      <div className="max-w-6xl mx-auto flex flex-col xl:flex-row gap-10 items-start">
        <div className="xl:w-2/5 flex-shrink-0">
          <SuccessStories />
        </div>
        <div className="xl:w-3/5 min-w-0 overflow-hidden">
          <TeamCarousel />
        </div>
      </div>

      {/* Licenciatura */}
      <LicStudents />

      {selectedResearcher && (
        <ResearcherModal
          researcher={selectedResearcher}
          onClose={() => setSelectedResearcher(null)}
        />
      )}
    </section>
  );
}